import {
	Component,
	EventEmitter,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { SubSink } from 'subsink';

// --- PrimeNG ---
import { MessageService } from 'primeng/api';

// --- Facades ---
import { CartFacade } from '@facades/cart.facade';

// --- Utils ---
import { isObjectChanged } from '@utils/diff.util';

// --- Interfaces ---
import type { ICartLocalStorage } from '@interfaces/cart';
import type { IProduct } from '@interfaces/product';
import type { IProductCart } from '@interfaces/productCart';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
	selector: 'app-cart-dialog',
	templateUrl: './cart-dialog.component.html',
	styleUrls: ['./cart-dialog.component.scss'],
})
export class CartDialogComponent implements OnInit, OnDestroy {
	parameters = JSON.parse(localStorage.getItem('parameters') as string);
	objectFit = 'cover';
	isRequesting = false;

	private subs = new SubSink();

	products: IProductCart[] = [];
	currentProducts: IProductCart[] = [];

	@Output() emptyCartEvent = new EventEmitter<void>();

	constructor(
		private _router: Router,
		public _ref: DynamicDialogRef,
		private _messageService: MessageService,
		public cartFacade: CartFacade
	) {
		this.objectFit = this.parameters?.allowResizeProductImages
			? 'contain'
			: 'cover';
	}

	ngOnInit(): void {
		this.products = this.cartFacade.getCartCache();
		this.currentProducts = this.products;
	}

	get storedCart() {
		return JSON.parse(localStorage.getItem('cart')!) as ICartLocalStorage;
	}

	saveCart() {
		if (!this.storedCart?.products) return;

		const changed = isObjectChanged(
			this.currentProducts,
			this.storedCart.products
		);
		if (!changed) return;

		this.subs.add(
			this.cartFacade.saveCart(this.storedCart.products).subscribe({
				next: items => {
					const cart = { ...this.storedCart, products: items.result };
					localStorage.setItem('cart', JSON.stringify(cart));
				},
				error: () => {
					this._messageService.add({
						severity: 'error',
						summary: 'Houve um problema!',
						detail: 'Não foi possível atualizar o carrinho.',
					});
				},
			})
		);
	}

	getQtd(qtd: number, product: IProduct, unitPrice: number) {
		product.prices = [{ price: unitPrice }];
		this.cartFacade.changeQtd(product, qtd);
	}

	trackByProduct(index: number, item: IProductCart) {
		return item.productId;
	}

	openCart() {
		this._ref.close(false);
		this._router.navigate(['account', 'cart']);
	}

	removeProduct(id: number) {
		const products = this.cartFacade.removeProduct(id);
		this.products = products || [];

		this.setRequesting();

		if (this.products.length <= 0) this.emptyCartEvent.emit();
	}

	setRequesting() {
		this.isRequesting = true;
		setTimeout(() => (this.isRequesting = false), 1000);
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
		setTimeout(() => this.saveCart(), 800);
	}
}
